import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { LanguageStore } from '@store';
import { AssistantFiltersData } from '@vi-iot/client/apis/assistant/assistant.api-model';
import { AssistantApiPicker } from '@vi-iot/client/apis/assistant/assistant.api-picker';
import { ViToastService } from '@vi-iot/ui';
import { isEqual } from 'lodash-es';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, retry, switchMap, tap } from 'rxjs/operators';
import { AssistantService } from '../../+assistant/assistant.service';
import { AppInsightsMonitoringService, LogLevelEnum } from '../../shared/services/app-insights';
import { APP_INSIGHTS_VIASSIST } from '../../shared/services/app-insights/events/assistant.events';
import { AssistantAction } from './assistant.action';
import { AssistantHelperService } from './assistant.helper.service';
import { AssistantDocumentType, AssistantImages } from './assistant.model';
import { AssistantStore } from './assistant.store';

@Injectable()
export class AssistantEffects {
  constructor(
    private actions$: Actions,
    private assistantApiPicker: AssistantApiPicker,
    private assistantStore: AssistantStore,
    private assistantService: AssistantService,
    private assistantHelperService: AssistantHelperService,
    private appInsights: AppInsightsMonitoringService,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private languageStore: LanguageStore
  ) {}

  getFilters$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.getFilters.attempt),
      concatLatestFrom(() => this.languageStore.languageCode$),
      switchMap(([action, langCode]) =>
        this.assistantApiPicker
          .instance()
          .getFilters(
            action.app,
            action.searchText,
            action.filtersPayload,
            this.assistantHelperService.getAssistantPayloadLanguage(langCode)
          )
          .pipe(
            retry(2),
            tap(() => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_FILTERS, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_FILTERS_SUCCESS,
                LogLevelEnum.ERROR
              );
            }),
            catchError((error) => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_FILTERS, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_FILTERS_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return throwError(error);
            }),
            map((response: AssistantFiltersData) => {
              return AssistantAction.getFilters.success({ filters: response.filters });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant('+ASSISTANT.SEARCH.GET_FILTERS_ERROR'),
              });

              return of(AssistantAction.getFilters.error({ error }));
            })
          )
      )
    );
  });

  setSearchQuery$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.setSearchQuery.attempt),
      concatLatestFrom(() => [this.assistantStore.searchQuery$]),
      map(([action, searchQuery]) => {
        return isEqual(action.query, searchQuery)
          ? AssistantAction.setSearchQuery.aborted()
          : AssistantAction.setSearchQuery.success({ query: action.query });
      })
    );
  });

  setAssistantFilters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.setAssistantFilters.attempt),
      concatLatestFrom(() => [this.assistantStore.selectedFilters$]),
      map(([action, selectedFilters]) => {
        return isEqual(action.filters, selectedFilters)
          ? AssistantAction.setAssistantFilters.aborted()
          : AssistantAction.setAssistantFilters.success({ filters: action.filters });
      })
    );
  });

  getAssistantFeedbackDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.assistantFeedbackData.attempt),
      concatLatestFrom(() => [this.assistantStore.assistantFeedbackState$]),
      switchMap(([_, feedbackState]) => {
        const limit = 100;

        return this.assistantApiPicker
          .instance()
          .getAssistantFeedbackData(
            feedbackState.filters.search ?? null,
            feedbackState.filters.startDate ?? null,
            feedbackState.filters.endDate ?? null,
            feedbackState.filters.sortBy ?? null,
            limit
          )
          .pipe(
            retry(2),
            map((response) =>
              AssistantAction.assistantFeedbackData.success({
                feedbackData: response.feedbacks,
                totalCount: response.total,
                limit,
              })
            ),
            catchError((error) => of(AssistantAction.assistantFeedbackData.error({ error })))
          );
      })
    );
  });

  fetchAssistantFeedbackDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.assistantFeedbackData.fetch),
      concatLatestFrom(() => [this.assistantStore.assistantFeedbackState$]),
      switchMap(([action, feedbackState]) => {
        if (feedbackState.feedbackData.canFetch) {
          const offset = feedbackState.feedbackData.value.length ?? 0;

          return this.assistantApiPicker
            .instance()
            .getAssistantFeedbackData(
              feedbackState.filters.search ?? null,
              feedbackState.filters.startDate ?? null,
              feedbackState.filters.endDate ?? null,
              feedbackState.filters.sortBy ?? null,
              action.maxItemsPerFetch,
              offset
            )
            .pipe(
              retry(2),
              map((response) =>
                AssistantAction.assistantFeedbackData.success({
                  feedbackData: response.feedbacks,
                  totalCount: response.total,
                  limit: action.maxItemsPerFetch,
                })
              ),
              catchError((error) => of(AssistantAction.assistantFeedbackData.error({ error })))
            );
        }

        return of(AssistantAction.assistantFeedbackData.noDataToFetch());
      })
    );
  });

  getSuggestions$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.getSuggestions.attempt),
      concatLatestFrom(() => this.languageStore.languageCode$),
      switchMap(([action, langCode]) =>
        this.assistantApiPicker
          .instance()
          .getSuggestion(
            action.query,
            action.app,
            this.assistantHelperService.getAssistantPayloadLanguage(langCode)
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_SUGGESTIONS, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_SUGGESTIONS_SUCCESS,
                LogLevelEnum.ERROR
              );
            }),
            catchError((error) => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_SUGGESTIONS, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_SUGGESTIONS_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return throwError(error);
            }),
            retry(2),
            map((response) => {
              return AssistantAction.getSuggestions.success({ suggestions: response });
            }),
            catchError((error) => {
              return of(AssistantAction.getSuggestions.error({ error }));
            })
          )
      )
    );
  });

  searchForDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.searchForDocuments.attempt),
      concatLatestFrom(() => this.languageStore.languageCode$),
      switchMap(([action, langCode]) =>
        this.assistantApiPicker
          .instance()
          .searchDocuments(
            action.text,
            action.app,
            action.filtersPayload,
            this.assistantHelperService.getAssistantPayloadLanguage(langCode),
            action.limit
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.SEARCH_FOR_DOCUMENTS,
                LogLevelEnum.ERROR
              );

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.SEARCH_FOR_DOCUMENTS_SUCCESS,
                LogLevelEnum.ERROR
              );
            }),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.SEARCH_FOR_DOCUMENTS,
                LogLevelEnum.ERROR
              );

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.SEARCH_FOR_DOCUMENTS_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return throwError(error);
            }),
            retry(2),
            map((response) => {
              //TODO - remove filter in ViAssist part 2 - VIG-16211
              const documents = response.results.filter(
                (response) => response.documentType.value !== AssistantDocumentType.decisionTree
              );

              return AssistantAction.searchForDocuments.success({
                documents: documents,
                totalCount: response.totalCount,
              });
            }),
            catchError((error) => {
              return of(AssistantAction.searchForDocuments.error({ error }));
            })
          )
      )
    );
  });

  fetchDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.searchForDocuments.fetch),
      concatLatestFrom(() => [
        this.assistantStore.searchQuery$,
        this.assistantStore.searchDocuments$,
        this.assistantStore.selectedFilters$,
        this.languageStore.languageCode$,
      ]),
      switchMap(([action, searchQuery, searchDocuments, selectedFilters, langCode]) => {
        if (searchDocuments.canFetch) {
          const offset = searchDocuments.value.length ?? 0;
          const filtersPayload = this.assistantService.mapFiltersToFilterPayload(selectedFilters);

          return this.assistantApiPicker
            .instance()
            .searchDocuments(
              searchQuery,
              action.app,
              filtersPayload,
              this.assistantHelperService.getAssistantPayloadLanguage(langCode),
              action.maxItemsPerFetch,
              offset
            )
            .pipe(
              retry(2),
              map((response) => {
                //TODO - remove filter in ViAssist part 2 - VIG-16211
                const documents = response.results.filter(
                  (response) => response.documentType.value !== AssistantDocumentType.decisionTree
                );

                return AssistantAction.searchForDocuments.success({
                  documents: documents,
                  totalCount: response.totalCount,
                });
              }),
              catchError((error) => of(AssistantAction.searchForDocuments.error({ error })))
            );
        } else {
          return of(AssistantAction.searchForDocuments.noDataToFetch());
        }
      })
    );
  });

  getDocumentDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AssistantAction.selectDocument.getDocumentDetails,
        AssistantAction.selectDocument.getDocumentDetailsWithUrlRedirection
      ),
      concatLatestFrom(() => [
        this.assistantStore.selectedDocumentDetails$,
        this.languageStore.languageCode$,
      ]),
      switchMap(([action, selectedDocumentDetailsState, langCode]) => {
        if (isEqual(action.documentId, selectedDocumentDetailsState.value?.id)) {
          return of(AssistantAction.selectDocument.getDocumentDetailsAborted());
        }

        return this.assistantApiPicker
          .instance()
          .getDocumentDetails(
            action.documentId,
            action.app,
            this.assistantHelperService.getAssistantPayloadLanguage(langCode)
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_DETAILS,
                LogLevelEnum.ERROR
              );

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_DETAILS_SUCCESS,
                LogLevelEnum.ERROR
              );
            }),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_DETAILS,
                LogLevelEnum.ERROR
              );

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_DETAILS_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return throwError(error);
            }),
            retry(2),
            map((response) => {
              if (
                action.type === '[Assistant Select document] getDocumentDetailsWithUrlRedirection'
              ) {
                return AssistantAction.selectDocument.getDocumentDetailsWithUrlRedirectionSuccess({
                  documentDetails: response,
                });
              }

              return AssistantAction.selectDocument.getDocumentDetailsSuccess({
                documentDetails: response,
              });
            }),
            catchError((error) => {
              return of(AssistantAction.selectDocument.getDocumentDetailsError({ error }));
            })
          );
      })
    );
  });

  getDocumentContentById$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.selectDocument.getContentByDocumentId),
      concatLatestFrom(() => [
        this.assistantStore.selectedDocumentDetails$,
        this.languageStore.languageCode$,
      ]),
      switchMap(([action, selectedDocumentDetailsState, langCode]) => {
        const documentType: AssistantDocumentType = this.assistantHelperService.getDocumentType(
          selectedDocumentDetailsState.value
        );

        return this.assistantApiPicker
          .instance()
          .getDocumentContent(
            selectedDocumentDetailsState.value.id,
            documentType === AssistantDocumentType.pdf,
            action.app,
            this.assistantHelperService.getAssistantPayloadLanguage(langCode)
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_DOCUMENT, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_SUCCESS,
                LogLevelEnum.ERROR
              );
            }),
            catchError((error) => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_DOCUMENT, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return throwError(error);
            }),
            retry(2),
            map((response) => {
              return AssistantAction.selectDocument.getContentSuccess({
                documentContent: response,
                app: action.app,
              });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+ASSISTANT.SEARCH.GET_DOCUMENT_CONTENT_ERROR'
                ),
              });

              return of(AssistantAction.selectDocument.getContentError({ error }));
            })
          );
      })
    );
  });

  getDocumentContent$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.selectDocument.getContent),
      concatLatestFrom(() => [
        this.assistantStore.searchDocument$,
        this.languageStore.languageCode$,
      ]),
      switchMap(([action, searchDocument, langCode]) => {
        const documentType: AssistantDocumentType = this.assistantHelperService.getDocumentType(
          searchDocument
        );

        return this.assistantApiPicker
          .instance()
          .getDocumentContent(
            searchDocument.id,
            documentType === AssistantDocumentType.pdf,
            action.app,
            this.assistantHelperService.getAssistantPayloadLanguage(langCode)
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_DOCUMENT, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_SUCCESS,
                LogLevelEnum.ERROR
              );
            }),
            catchError((error) => {
              this.appInsights.logEvent(APP_INSIGHTS_VIASSIST.GET_DOCUMENT, LogLevelEnum.ERROR);

              this.appInsights.logEvent(
                APP_INSIGHTS_VIASSIST.GET_DOCUMENT_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return throwError(error);
            }),
            retry(2),
            map((response) => {
              return AssistantAction.selectDocument.getContentSuccess({
                documentContent: response,
                app: action.app,
              });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+ASSISTANT.SEARCH.GET_DOCUMENT_CONTENT_ERROR'
                ),
              });

              return of(AssistantAction.selectDocument.getContentError({ error }));
            })
          );
      })
    );
  });

  checkForImages$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.selectDocument.getContentSuccess),
      concatLatestFrom(() => [this.assistantStore.selectedDocumentState$]),
      switchMap(([action, selectedDocumentState]) => {
        const document =
          selectedDocumentState.searchDocument ?? selectedDocumentState.details.value;

        const documentType: AssistantDocumentType = this.assistantHelperService.getDocumentType(
          document
        );

        return of(
          documentType === AssistantDocumentType.pdf
            ? AssistantAction.selectDocument.getImagesAborted()
            : AssistantAction.selectDocument.getImages({ app: action.app })
        );
      })
    );
  });

  getDocumentImages$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.selectDocument.getImages),
      concatLatestFrom(() => [this.assistantStore.selectedDocumentState$]),
      switchMap(([action, selectedDocumentState]) => {
        const imagesSrc: string[] = this.assistantHelperService.getImagesSrcFromHtmlContent(
          selectedDocumentState.content.value
        );
        if (imagesSrc.length > 0) {
          const resourceId = this.assistantHelperService.getResourceId(
            selectedDocumentState.searchDocument
          );

          const requests = imagesSrc.map((url) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_VIASSIST.GET_DOCUMENT_IMAGES,
              LogLevelEnum.ERROR
            );

            // It's difficult to get notified about event cancellation with forkJoin.
            // Due to pressure of hotfix I decided go with sync + async approach which is confirmed by UT
            let isResponseUndefined = true;

            return this.assistantApiPicker
              .instance()
              .getResource(resourceId, url, action.app)
              .pipe(
                map((blob) => ({
                  name: url,
                  blob,
                })),
                tap(() => {
                  this.appInsights.logEvent(
                    APP_INSIGHTS_VIASSIST.GET_DOCUMENT_IMAGES_SUCCESS,
                    LogLevelEnum.ERROR
                  );

                  isResponseUndefined = false;
                }),
                catchError((error) => {
                  this.appInsights.logEvent(
                    APP_INSIGHTS_VIASSIST.GET_DOCUMENT_IMAGES_ERROR,
                    LogLevelEnum.ERROR,
                    { error }
                  );

                  isResponseUndefined = false;

                  return throwError(error);
                }),
                finalize(() => {
                  if (isResponseUndefined) {
                    this.appInsights.logEvent(
                      APP_INSIGHTS_VIASSIST.GET_DOCUMENT_IMAGES_CANCELED,
                      LogLevelEnum.ERROR
                    );
                  }
                })
              );
          });

          return forkJoin(requests).pipe(
            map((responses: AssistantImages[]) => {
              if (responses.length > 0) {
                return AssistantAction.selectDocument.getImagesSuccess({ images: responses });
              } else {
                return AssistantAction.selectDocument.getImagesAborted();
              }
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+ASSISTANT.SEARCH.GET_DOCUMENT_IMAGES_ERROR'
                ),
              });

              return of(AssistantAction.selectDocument.getImagesError({ error }));
            })
          );
        }

        return of(AssistantAction.selectDocument.getImagesAborted());
      })
    );
  });

  parseDocumentContent$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.selectDocument.getImagesSuccess),
      concatLatestFrom(() => [this.assistantStore.selectedDocumentState$]),
      switchMap(([_action, selectedDocumentState]) => {
        const parsedContent = this.assistantHelperService.replaceImagesInHtmlContent(
          selectedDocumentState.content.value,
          selectedDocumentState.images.value
        );

        return of(
          AssistantAction.selectDocument.parseDocumentContent({
            documentContent: parsedContent,
          })
        );
      })
    );
  });
}
